import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  LinearProgress,
  Notice,
  Segmented,
  Skeleton,
  Switch,
  StateScreen,
  TextArea,
  TextField,
  useSnackbar,
  DateField,
} from '@sauda/ui/kit';
import {
  CLAUSE_KEYS,
  DEFAULT_CLAUSES,
  DEFAULT_REFUND_DAYS,
  clauseTitle,
  isClauseKey,
  type AgreementData,
  type ClauseKey,
} from '@sauda/templates';
import { Screen, useChrome } from '../../app/chrome.js';
import { useAuth } from '../../auth/AuthContext.js';
import { formatCalendarDate } from '../../lib/time.js';
import { DraftPreviewSheet, ReviewSheet } from './AgreementDraftSheets.js';
import { ApiError, api } from '../../lib/api.js';
import type { AgreementView, DealDetailView } from '../../lib/types.js';
import { formatRupees, paiseToRupeesInput, rupeesToPaise } from '../../lib/money.js';

interface Estimate {
  stampDutyPaise: number;
  registrationFeePaise: number;
  documentHandlingPaise: number;
  totalPaise: number;
  rulesVersion: string;
  label: string;
}

/** The estimate, always labelled as an estimate and carrying the version of the rules it came from. */
export function EstimateCard({ estimate }: { estimate: Estimate }) {
  const { t } = useTranslation();
  return (
    <div className="estimate" data-testid="estimate">
      <p className="k-t-label-sm k-muted">
        {estimate.label} · {t('deals.rulesVersion', { version: estimate.rulesVersion })}
      </p>
      <p className="k-t-body">
        {t('deals.stampDuty')}: {formatRupees(estimate.stampDutyPaise)}
      </p>
      <p className="k-t-body">
        {t('deals.regFee')}: {formatRupees(estimate.registrationFeePaise)}
      </p>
      <p className="k-t-body">
        {t('deals.handling')}: {formatRupees(estimate.documentHandlingPaise)}
      </p>
      <p className="k-t-title-sm">
        {t('deals.estimateTotal')}: {formatRupees(estimate.totalPaise)}
      </p>
    </div>
  );
}

/** The agreement form with a live estimate that updates as the money fields change. Nothing is saved by the estimate. */
export function AgreementScreen() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const chrome = useChrome();
  const auth = useAuth();
  const deal = useQuery({
    queryKey: ['deal', id],
    queryFn: () => api<DealDetailView>(`/deals/${id}`),
  });
  const agreement = useQuery({
    queryKey: ['agreement', id],
    queryFn: () => api<AgreementView | null>(`/deals/${id}/agreement`),
    // The draft is made by the worker a moment after saving.
    refetchInterval: (q) => (q.state.data != null && !q.state.data.draftReady ? 1500 : false),
  });
  const [form, setForm] = useState({
    licensor: '',
    licensee: '',
    address: '',
    startDate: '',
    termMonths: '11',
    rent: '',
    deposit: '',
    escalation: '0',
    lockIn: '0',
    notice: '30',
    use: 'Residential',
    extra: '',
  });
  const [locality, setLocality] = useState<'urban' | 'rural'>('urban');
  const [lang, setLang] = useState<'en' | 'mr' | 'hi'>('en');
  const [clauses, setClauses] = useState<ClauseKey[]>(DEFAULT_CLAUSES);
  const [refundDays, setRefundDays] = useState(String(DEFAULT_REFUND_DAYS));
  const [previewing, setPreviewing] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<Estimate | null>(null);

  // Start from the saved agreement, or for a renewal from the old terms with the escalation applied.
  useEffect(() => {
    if (seeded || deal.data === undefined || agreement.data === undefined) return;
    const a = agreement.data;
    const p = deal.data.renewalPrefill;
    if (a !== null) {
      setForm({
        licensor: String(a.details['licensor'] ?? ''),
        licensee: String(a.details['licensee'] ?? ''),
        address: String(a.details['address'] ?? ''),
        startDate: a.startDate,
        termMonths: String(a.termMonths),
        rent: paiseToRupeesInput(a.rentPaise),
        deposit: paiseToRupeesInput(a.depositPaise),
        escalation: String(a.details['escalationPercent'] ?? 0),
        lockIn: String(a.details['lockInMonths'] ?? 0),
        notice: String(a.details['noticeDays'] ?? 30),
        use: String(a.details['permittedUse'] ?? 'Residential'),
        extra: String(a.details['extraClauses'] ?? ''),
      });
      setLocality(a.locality === 'rural' ? 'rural' : 'urban');
      const savedLang = a.details['lang'];
      setLang(savedLang === 'mr' || savedLang === 'hi' ? savedLang : 'en');
      const savedClauses = a.details['clauses'];
      setClauses(Array.isArray(savedClauses) ? savedClauses.filter(isClauseKey) : []);
      setRefundDays(String(a.details['refundDays'] ?? DEFAULT_REFUND_DAYS));
    } else if (p !== null) {
      setForm({
        licensor: String(p.details['licensor'] ?? ''),
        licensee: String(p.details['licensee'] ?? ''),
        address: String(p.details['address'] ?? ''),
        startDate: p.startDate,
        termMonths: String(p.termMonths),
        rent: paiseToRupeesInput(p.rentPaise),
        deposit: paiseToRupeesInput(p.depositPaise),
        escalation: String(p.escalationPercent),
        lockIn: String(p.details['lockInMonths'] ?? 0),
        notice: String(p.details['noticeDays'] ?? 30),
        use: 'Residential',
        extra: '',
      });
    } else {
      const prefill = deal.data.propertyPrefill;
      setForm((f) => ({
        ...f,
        licensor: deal.data?.owner.name ?? '',
        licensee: deal.data?.client.name ?? '',
        address: prefill.address,
        startDate: prefill.startDate ?? f.startDate,
        rent: prefill.rentPaise === null ? f.rent : paiseToRupeesInput(prefill.rentPaise),
        deposit:
          prefill.depositPaise === null ? f.deposit : paiseToRupeesInput(prefill.depositPaise),
      }));
    }
    // A new draft starts in the client's language.
    if (a === null) setLang(deal.data.lang);
    setSeeded(true);
  }, [seeded, deal.data, agreement.data]);

  // The live estimate: wait for the person to pause, ask the server, and keep the last good answer while the form is incomplete.
  useEffect(() => {
    const rent = rupeesToPaise(form.rent);
    const deposit = rupeesToPaise(form.deposit);
    const months = Number(form.termMonths);
    if (
      rent === null ||
      rent <= 0 ||
      deposit === null ||
      !Number.isInteger(months) ||
      months < 1 ||
      months > 60
    )
      return;
    const timer = setTimeout(() => {
      void api<Estimate>(`/deals/${id}/agreement/estimate`, {
        method: 'POST',
        body: { termMonths: months, rentPaise: rent, depositPaise: deposit, locality },
      }).then(setEstimate, () => undefined);
    }, 400);
    return () => clearTimeout(timer);
  }, [form.rent, form.deposit, form.termMonths, locality, id]);

  const set =
    (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });
  const save = useMutation({
    mutationFn: () =>
      api(`/deals/${id}/agreement`, {
        method: 'PUT',
        body: {
          termMonths: Number(form.termMonths),
          startDate: form.startDate,
          rentPaise: rupeesToPaise(form.rent),
          depositPaise: rupeesToPaise(form.deposit),
          locality,
          details: {
            licensor: form.licensor.trim(),
            licensee: form.licensee.trim(),
            address: form.address.trim(),
            escalationPercent: Number(form.escalation),
            lockInMonths: Number(form.lockIn),
            noticeDays: Number(form.notice),
            permittedUse: form.use.trim() === '' ? 'Residential' : form.use.trim(),
            ...(form.extra.trim() === '' ? {} : { extraClauses: form.extra.trim() }),
            lang,
            clauses,
            refundDays: Number(refundDays) || DEFAULT_REFUND_DAYS,
          },
        },
      }),
    onSuccess: async () => {
      setError(null);
      await cache.invalidateQueries({ queryKey: ['agreement', id] });
      snack({ text: t('deal.agreementSaved') });
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.status < 500 && e.message !== '' ? e.message : t('deal.generic'),
      ),
  });
  const drafted = useMutation({
    mutationFn: () =>
      api(`/deals/${id}/transition`, { method: 'POST', body: { to: 'agreement_drafted' } }),
    onSuccess: async () => {
      await Promise.all([
        cache.invalidateQueries({ queryKey: ['deal', id] }),
        cache.invalidateQueries({ queryKey: ['deals'] }),
      ]);
      void navigate(`/deals/${id}`);
    },
    onError: () => setError(t('deal.generic')),
  });

  if (deal.isPending || agreement.isPending)
    return (
      <Screen title={t('deal.agreement')} back={`/deals/${id}`}>
        <Skeleton height={200} />
      </Screen>
    );
  if (deal.isError || deal.data === undefined)
    return (
      <Screen title={t('deal.agreement')} back="/deals">
        <StateScreen kind="error" title={t('deal.notFound')} />
      </Screen>
    );
  const a = agreement.data ?? null;
  const previewData: AgreementData = {
    agencyName: auth.current?.agencyName ?? '',
    licensor: form.licensor.trim(),
    licensee: form.licensee.trim(),
    address: form.address.trim(),
    termMonths: Number(form.termMonths) || 11,
    startDate: form.startDate === '' ? new Date().toISOString().slice(0, 10) : form.startDate,
    rentPaise: rupeesToPaise(form.rent) ?? 0,
    depositPaise: rupeesToPaise(form.deposit) ?? 0,
    escalationPercent: Number(form.escalation) || 0,
    lockInMonths: Number(form.lockIn) || 0,
    noticeDays: Number(form.notice) || 0,
    permittedUse: form.use.trim() === '' ? 'Residential' : form.use.trim(),
    extraClauses: form.extra.trim() === '' ? null : form.extra.trim(),
    lang,
    clauses,
    refundDays: Number(refundDays) || DEFAULT_REFUND_DAYS,
  };
  const valid =
    form.licensor.trim().length >= 2 &&
    form.licensee.trim().length >= 2 &&
    form.address.trim().length >= 5 &&
    /^\d{4}-\d{2}-\d{2}$/.test(form.startDate) &&
    (rupeesToPaise(form.rent) ?? 0) > 0 &&
    rupeesToPaise(form.deposit) !== null;
  return (
    <Screen
      title={t('deal.agreement')}
      eyebrow={deal.data.propertyTitle ?? undefined}
      back={`/deals/${id}`}
    >
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      <Card>
        <h2 className="k-t-title-sm">{t('deal.agreementParties')}</h2>
        <TextField label={t('deals.licensor')} value={form.licensor} onChange={set('licensor')} />
        <TextField label={t('deals.licensee')} value={form.licensee} onChange={set('licensee')} />
        <TextField label={t('deals.address')} value={form.address} onChange={set('address')} />
      </Card>
      <Card>
        <h2 className="k-t-title-sm">{t('deal.agreementTerm')}</h2>
        <DateField
          label={t('deals.startDate')}
          value={form.startDate}
          onChange={(iso) => setForm({ ...form, startDate: iso })}
        />
        <TextField
          label={t('deals.termMonths')}
          inputMode="numeric"
          value={form.termMonths}
          onChange={set('termMonths')}
        />
        <TextField
          label={t('deals.rent')}
          prefix="₹"
          inputMode="decimal"
          value={form.rent}
          onChange={set('rent')}
        />
        <TextField
          label={t('deals.deposit')}
          prefix="₹"
          inputMode="decimal"
          value={form.deposit}
          onChange={set('deposit')}
        />
        <TextField
          label={t('deals.escalation')}
          inputMode="decimal"
          value={form.escalation}
          onChange={set('escalation')}
        />
        <TextField
          label={t('deals.lockIn')}
          inputMode="numeric"
          value={form.lockIn}
          onChange={set('lockIn')}
        />
        <TextField
          label={t('deals.notice')}
          inputMode="numeric"
          value={form.notice}
          onChange={set('notice')}
        />
      </Card>
      <Card>
        <h2 className="k-t-title-sm">{t('deal.agreementUse')}</h2>
        <TextField label={t('deal.permittedUse')} value={form.use} onChange={set('use')} />
        <TextArea label={t('deal.extraClauses')} value={form.extra} onChange={set('extra')} />
        <span className="k-t-label-lg">{t('deal.area')}</span>
        <Segmented
          label={t('deal.area')}
          value={locality}
          onChange={(v) => setLocality(v as 'urban' | 'rural')}
          options={[
            { value: 'urban', label: t('deal.urban') },
            { value: 'rural', label: t('deal.rural') },
          ]}
        />
      </Card>
      <Card>
        <h2 className="k-t-title-sm">{t('deal.clausesTitle')}</h2>
        <p className="k-t-body k-muted">{t('deal.clausesHelp')}</p>
        <span className="k-t-label-lg">{t('deal.draftLanguage')}</span>
        <Segmented
          label={t('deal.draftLanguage')}
          value={lang}
          onChange={(v) => setLang(v as 'en' | 'mr' | 'hi')}
          options={[
            { value: 'en', label: 'English' },
            { value: 'mr', label: 'मराठी' },
            { value: 'hi', label: 'हिन्दी' },
          ]}
        />
        {lang === 'en' ? null : <Notice tone="info">{t('deal.draftLanguageReview')}</Notice>}
        {CLAUSE_KEYS.map((key) => (
          <Switch
            key={key}
            label={clauseTitle(key, 'en')}
            checked={clauses.includes(key)}
            onChange={(e) =>
              setClauses(
                e.target.checked
                  ? CLAUSE_KEYS.filter((k) => k === key || clauses.includes(k))
                  : clauses.filter((k) => k !== key),
              )
            }
          />
        ))}
        {clauses.includes('depositRefund') ? (
          <TextField
            label={t('deal.refundDays')}
            inputMode="numeric"
            value={refundDays}
            onChange={(e) => setRefundDays(e.target.value.replace(/\D/g, ''))}
          />
        ) : null}
      </Card>
      {estimate === null ? (
        a === null ? (
          <Card>
            <p className="k-t-body k-muted">{t('deal.estimateHint')}</p>
          </Card>
        ) : (
          <EstimateCard estimate={a.estimate} />
        )
      ) : (
        <EstimateCard estimate={estimate} />
      )}
      <Button size="lg" block variant="outlined" onClick={() => setPreviewing(true)}>
        {t('deal.previewDraft')}
      </Button>
      <Button
        size="lg"
        block
        loading={save.isPending}
        disabled={!valid || chrome.readOnly || deal.data.state === 'registered'}
        onClick={() => save.mutate()}
      >
        {t('deals.saveAgreement')}
      </Button>
      {a === null ? null : a.draftReady ? (
        <Card>
          <div className="chips-row">
            {a.draftUrl === null ? null : (
              <a
                className="k-btn k-btn--tonal k-btn--md k-layer k-t-label-lg"
                href={a.draftUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('deals.openDraft')}
              </a>
            )}
            {deal.data.state === 'registered' || chrome.readOnly ? null : (
              <Button variant="tonal" onClick={() => setReviewing(true)}>
                {t('deal.sendForReview')}
              </Button>
            )}
            {deal.data.state === 'memo_signed' ? (
              <Button loading={drafted.isPending} onClick={() => drafted.mutate()}>
                {t('deal.markDrafted')}
              </Button>
            ) : null}
          </div>
          {a.reviewSentAt === null ? null : (
            <p className="k-t-body-sm k-muted">
              {t('deal.reviewSentOn', { date: formatCalendarDate(a.reviewSentAt.slice(0, 10)) })}
            </p>
          )}
        </Card>
      ) : (
        <Card>
          <p className="k-t-body">{t('deals.draftPreparing')}</p>
          <LinearProgress label={t('deals.draftPreparing')} />
        </Card>
      )}
      <DraftPreviewSheet
        open={previewing}
        onClose={() => setPreviewing(false)}
        data={previewData}
        estimate={estimate ?? a?.estimate ?? null}
      />
      <ReviewSheet
        open={reviewing}
        onClose={() => setReviewing(false)}
        dealId={id}
        agency={auth.current?.agencyName ?? ''}
        property={deal.data.propertyTitle ?? ''}
        tenantPhone={deal.data.client.phone}
        ownerPhone={deal.data.owner.phone}
        onSent={() => void cache.invalidateQueries({ queryKey: ['agreement', id] })}
      />
    </Screen>
  );
}
